import { 
  useUpdateSettingsMutation, 
  useUpdateCurrencyMutation, 
  useUpdateExVatMutation 
} from '../settingsApi';
import { settingsApiSlice } from '../settingsApi';
import { useDispatch, useStore } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { TOAST_ERROR, TOAST_SUCCESS } from '../../../utils/constants';
import i18next from 'i18next';

const getSettingsData = (getState) => {
  const state = getState();
  return state.settingsApi.queries['fetchSettings(undefined)']?.data || null;
};

const updateSettingsCache = (dispatch, settingsData) => {
  dispatch(settingsApiSlice.util.updateQueryData('fetchSettings', undefined, (draft) => {
    if (draft) {
      Object.assign(draft, settingsData);
    } else {
      return settingsData;
    }
  }));
};

export const useSettingsActions = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const [updateSettingsTrigger, { isLoading: isUpdatingSettings }] = useUpdateSettingsMutation();
  const [updateCurrencyTrigger] = useUpdateCurrencyMutation();
  const [updateExVatTrigger] = useUpdateExVatMutation();

  const updateSettings = async (settingsData) => {
    try {
      const result = await updateSettingsTrigger(settingsData).unwrap();
      enqueueSnackbar(i18next.t('settings.settingsUpdated'), { variant: TOAST_SUCCESS });
      updateSettingsCache(dispatch, result);
      return result;
    } catch (err) {
      const errorMessage = err?.error?.response?.errMsg || 'Failed to update settings';
      enqueueSnackbar(errorMessage, { variant: TOAST_ERROR });
      throw err;
    }
  };

  const updateCurrency = async (currency) => {
    try {
      const result = await updateCurrencyTrigger(currency).unwrap();
      enqueueSnackbar(i18next.t('settings.currencyUpdated'), { variant: TOAST_SUCCESS });
      const currentSettings = getSettingsData(store.getState);
      if (currentSettings) {
        updateSettingsCache(dispatch, { ...currentSettings, currency: result.currency });
      }
      return result;
    } catch (err) {
      const errorMessage = err?.error?.response?.errMsg || 'Failed to update currency';
      enqueueSnackbar(errorMessage, { variant: TOAST_ERROR });
      throw err;
    }
  };

  const updateExVat = async ({ exVat }) => {
    try {
      const result = await updateExVatTrigger({ exVat }).unwrap();
      enqueueSnackbar(result.message, { variant: TOAST_SUCCESS });
      return result;
    } catch (err) {
      enqueueSnackbar(i18next.t('settings.updateFailed'), { variant: TOAST_ERROR });
      throw err;
    }
  };

  return {
    updateSettings,
    updateCurrency,
    updateExVat,
    isUpdatingSettings,
  };
};
